import { useCallback, useMemo } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

const NODE_COLORS = {
  trigger: "#0033CC",
  agent: "#059669",
  action: "#7C3AED",
  output: "#F59E0B",
  data: "#0EA5E9",
  integration: "#EC4899",
};

function AgentNode({ data }) {
  const bgColor = NODE_COLORS[data.nodeType] || "#0033CC";
  return (
    <div style={{ background: bgColor, color: "white", padding: "12px 16px", minWidth: 180, fontFamily: "'Outfit', sans-serif", fontSize: 12, borderRadius: 0 }} data-testid={`node-${data.label?.replace(/\s/g, "-")}`}>
      <div style={{ fontSize: 9, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>{data.nodeType || "System"}</div>
      <div style={{ fontWeight: 500, fontSize: 13, marginBottom: data.description ? 4 : 0 }}>{data.label}</div>
      {data.description && <div style={{ fontSize: 10, opacity: 0.8, lineHeight: 1.4 }}>{data.description}</div>}
    </div>
  );
}

const nodeTypes = { agent: AgentNode };

function buildNodesAndEdges(agentArch, systemDesigns) {
  const nodes = [];
  const edges = [];

  if (agentArch?.agents) {
    // Center row: agents
    const agents = agentArch.agents;
    const startX = 50;
    const spacing = 280;

    // Data sources row (top)
    nodes.push({ id: "data-hub", position: { x: startX + (agents.length - 1) * spacing / 2, y: 0 }, type: "agent", data: { label: "Data Intelligence Hub", nodeType: "data", description: "CRM + Website + Email + Calendar" } });

    agents.forEach((agent, i) => {
      const id = `agent-${i}`;
      nodes.push({
        id,
        position: { x: startX + i * spacing, y: 140 },
        type: "agent",
        data: {
          label: agent.name || `Agent ${i + 1}`,
          nodeType: "agent",
          description: agent.role || "",
        },
      });
      edges.push({ id: `e-data-${id}`, source: "data-hub", target: id, style: { stroke: "#94A3B8" }, animated: true });

      // Actions below each agent
      (agent.actions || []).slice(0, 2).forEach((action, j) => {
        const actionId = `action-${i}-${j}`;
        nodes.push({
          id: actionId,
          position: { x: startX + i * spacing + (j * 140 - 70), y: 290 },
          type: "agent",
          data: { label: action, nodeType: "action" },
        });
        edges.push({ id: `e-${id}-${actionId}`, source: id, target: actionId, style: { stroke: "#94A3B8" } });
      });
    });

    // Output row (bottom)
    nodes.push({ id: "output", position: { x: startX + (agents.length - 1) * spacing / 2, y: 420 }, type: "agent", data: { label: "Revenue & Growth Outcomes", nodeType: "output", description: "Leads, Conversions, Retention, Efficiency" } });
    agents.forEach((_, i) => {
      edges.push({ id: `e-agent-${i}-out`, source: `agent-${i}`, target: "output", style: { stroke: "#94A3B8", strokeDasharray: "5 5" } });
    });
  } else if (systemDesigns?.systems) {
    // Fallback: show systems as a flow
    systemDesigns.systems.forEach((sys, i) => {
      nodes.push({
        id: `sys-${i}`,
        position: { x: 50 + (i % 2) * 350, y: Math.floor(i / 2) * 200 },
        type: "agent",
        data: { label: sys.name || `System ${i + 1}`, nodeType: i === 0 ? "trigger" : "agent", description: sys.purpose || "" },
      });
      if (i > 0) {
        edges.push({ id: `e-sys-${i - 1}-${i}`, source: `sys-${i - 1}`, target: `sys-${i}`, style: { stroke: "#94A3B8" }, animated: true });
      }
    });
  }

  return { nodes, edges };
}

export default function ArchitectureDiagram({ agentArchitecture, systemDesigns }) {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => buildNodesAndEdges(agentArchitecture, systemDesigns),
    [agentArchitecture, systemDesigns]
  );

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  if (!initialNodes.length) return null;

  return (
    <div style={{ width: "100%", height: 500, border: "1px solid #E2E8F0", background: "#FAFAFA" }} data-testid="architecture-diagram">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#E2E8F0" gap={20} />
        <Controls showInteractive={false} />
        <MiniMap nodeColor={(n) => NODE_COLORS[n.data?.nodeType] || "#94A3B8"} style={{ border: "1px solid #E2E8F0" }} />
      </ReactFlow>
    </div>
  );
}
